import { HttpClient } from "@angular/common/http";
import { Component, OnInit } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { environment } from "src/environments/environment";
import { ApiResponse, CheckDto, SessionCheckDto, VehicleFleetCheckApiResponse } from "../check";
import { ActivatedRoute, Router } from "@angular/router";
import { branchOutcomes, ReconciliationDto, vehiclePresenceOutcomes } from "../../reconciliations/reconciliation";
import { IApiResponse } from "county-api";
import { BranchSchedule } from "../../branch-day-fleet-site-check";
import { NotificationsService } from "src/app/universal/notifications/notifications.service";


@Component({
    selector: 'fleet-settings',
    templateUrl: './check-list.component.html',
    styleUrls: ['./check-list.component.scss']
})
export class CheckListComponent implements OnInit {


    locationSelected = "Stockport";
    checkTypeSelected = "";
    dateSelected = new Date().toDateString();

    selectedDateIsToday = false;
    loading = 0;

    readonly locations: string[] = [
        "Stockport",
        "Oldham",
        "Preston",
        "Chester"
    ];

    readonly checkTypes: string[] = [
        "Vehicle Presence Check",
        "Vehicle Key Check",
        "Vehicle Locked Check"
    ];

    vehicleChecks: VehicleFleetCheckApiResponse[] = [];
    uncheckedVehicles: string[] = [];
    vehicleArrivalsFromPresenceChecks: string[] = [];
    vehicleDeparturesFromPresenceChecks: string[] = [];
    vehiclesFromPresenceCheckStillAvailable: string[] = [];
    locationMismatchedPresenceChecks: CheckDto[] = []; // Presence checks for vehicles that were and are available at the current location but have been checked in at another location
    locationMismatchedPresenceReconciliations: ReconciliationDto[] = []; // Presence reconciliations for vehicles that were and are available at the current location but have been checked in at another location and reconciled at the original location
    sessionCheck: SessionCheckDto | null = null; 

    vehicleArrivalsFromKeyChecks: string[] = [];
    vehicleDeparturesFromKeyChecks: string[] = [];
    vehiclesFromKeyCheckStillAvailable: string[] = []

    vehicleArrivalsFromLockedChecks: string[] = [];
    vehicleDeparturesFromLockedChecks: string[] = [];
    vehiclesFromLockedCheckStillAvailable: string[] = []

    isScheduledDateSelected = false;
    
    /**
     *
     */
    constructor(private authService: MsalService,
                private httpservice: HttpClient,
            private route: ActivatedRoute,
            private router: Router,
            private notificationService: NotificationsService) {        
    }

    ngOnInit(): void {
        const params = this.route.snapshot.queryParams;

        if(params.location){
            this.applySearchParams(params.location, params.year, params.month, params.day);
        }
        else{
            this.locationSelected = "Stockport";
            this.checkTypeSelected = this.checkTypes[0];
        }
        this.retrieveActiveLocationChecks();
    }

    applySearchParams(location: string | undefined, year: string | undefined, month: string  | undefined, day: string  | undefined){
        if (location){
            this.locationSelected = location;
        }

        if (year && month && day){
            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            this.dateSelected = date.toDateString();
        }
    }

    onDateChanged(dateString: string){
        const today = new Date();
        const givenDate = new Date(dateString);
        const isFutureDate = givenDate > today;
        if(isFutureDate){
            this.notificationService.warn("Please enter a date in the past or today's date.");
        }
        if (!dateString){
            return;
        }

        this.retrieveActiveLocationChecks();
    }
    onSelectionChanged(location: string){
        this.retrieveActiveLocationChecks();
    }

    redirectToReconciliation(reg: string){
        const path = '/site-checks/reconcile';

        const url = `${path}?reg=${reg}&checkindate=${this.selectedDateAsUnixTime}&location=${this.locationSelected}&type=${this.checkTypeSelected.replace(/ /g, "-")}`;

        this.router.navigateByUrl(url);
    }

    get selectedDateAsUnixTime(){
        const date = new Date(this.dateSelected);

        return Math.floor(date.getTime() / 1000);
    }

    get vehicleCheckDtos(): CheckDto[]{
       return this.vehicleChecks
       .flatMap(x => this.mapVehicleCheck(x))
       .filter(x => x.check_in_user != null && x.check_in_user.length > 0 && this.isRightCheckType(x, this.checkTypeSelected));
    }

    get reconciliationDtos(): ReconciliationDto[]{
       return this.vehicleChecks
       .flatMap(x => this.mapVehicleReconciliation(x))
       .filter(x => this.containsReconciliationOutcome(x));
    }

    containsReconciliationOutcome(dto: ReconciliationDto): boolean{
        return (dto.vehicle_presence_check_reconciliation_outcome != undefined && dto.vehicle_presence_check_reconciliation_outcome?.length > 0) 
        || (dto.vehicle_key_check_reconciliation_outcome != undefined && dto.vehicle_key_check_reconciliation_outcome?.length > 0)
        || (dto.vehicle_is_locked_check_reconciliation_outcome != undefined && dto.vehicle_is_locked_check_reconciliation_outcome?.length > 0)
    }

    // vehicles checked in and vehicles reconciliated
    get successfulPresenceChecks(){
        return this.vehicleCheckDtos.filter(x => x.check_status == "Vehicle Present");
    }

    get firstSuccessfulPresenceCheck(){
        if (this.successfulPresenceChecks.length == 0){
            return null;
        }
        const sortedList = this.successfulPresenceChecks.sort((a, b) => Date.parse(a.created_date) - Date.parse(b.created_date));
        return sortedList[0];
    }

    get presenceReconciliations(){
        let fullList = [];
    
        let standardReconciliations =  this.reconciliationDtos.filter(x => x.check_type == "Vehicle Presence Check");
        fullList.push(...standardReconciliations);

        if(this.locationMismatchedPresenceReconciliations.length > 0){
            
            for(let item of this.locationMismatchedPresenceReconciliations){
                if (fullList.map(x => x.Reg).indexOf(item.Reg) < 0){
                    fullList.push(item);
                }
            }
        }

        return fullList;
    }

    get successfulKeyChecks(){
        return this.vehicleCheckDtos.filter(x => x.check_status == "Key Present");
    }

    get sortedSuccessfulKeyChecks(){
        if (this.successfulKeyChecks.length == 0){
            return null;
        }
        return this.successfulKeyChecks.sort((a, b) => Date.parse(a.created_date) - Date.parse(b.created_date));
    }

    get keyReconciliations(){
        return this.reconciliationDtos.filter(x => x.check_type == "Vehicle Key Check");
    }

    get successfulLockedChecks(){
        return this.vehicleCheckDtos.filter(x => x.check_status == "Vehicle Locked");
    }

    get sortedSuccessfulLockedChecks(){
        if (this.successfulLockedChecks.length == 0){
            return null;
        }
        return this.successfulLockedChecks.sort((a, b) => Date.parse(a.created_date) - Date.parse(b.created_date));
    }

    get lockedReconciliations(){
        return this.reconciliationDtos.filter(x => x.check_type == "Vehicle Locked Check");
    }

    get dateObject(){
        return new Date(this.dateSelected);
    }

    get selectedYear(){
        return this.dateObject.getFullYear();
    }

    get selectedMonth(){
        return this.dateObject.getMonth() + 1;
    }

    get selectedDay(){
        return this.dateObject.getDate();
    }

    get vehiclesMissedChecks(){        

        // logic here is that a vehicle rentman located at the current branch: needs either a check or a reconciliation

        let missingVehicleChecks = [];

        // 2. Vehicles that 
        // a) were available at check time and are still available, 
        // b) have no check at the current location
        // c) have no reconciliation at the current location, 

        const presenceCheckRegs = this.successfulPresenceChecks.map(y => y.reg);

        const reconciledRegs = this.presenceReconciliations.map(x => x.Reg);

        const formerAndCurrentlyAvailableVehiclesNotReconciledAndNotChecked = 
            this.vehiclesFromPresenceCheckStillAvailable
            .filter(x => reconciledRegs.indexOf(x) < 0)
            .filter(x => presenceCheckRegs.indexOf(x) < 0);

        missingVehicleChecks.push(...formerAndCurrentlyAvailableVehiclesNotReconciledAndNotChecked);

        const finalList: string[] = Array.from(new Set([...missingVehicleChecks]));

        return finalList;
    }

    get vehiclesReconciliated(){
        return [];
    }

    get vehiclesMissedKeyChecks(){
        // out of those vehicles that were available at check time and are still available 
        // return those that have not been key checked in
        // and those that have not been reconciliated
        return this.vehiclesFromKeyCheckStillAvailable
        .filter(x => this.successfulKeyChecks.map(y => y.reg).indexOf(x) < 0)
        .filter(x => this.keyReconciliations.map(y => y.Reg).indexOf(x) < 0);
    }

    get vehicleArrivalsFromKeyChecksSource(){
        return this.vehicleArrivalsFromKeyChecks
        .filter(x => this.keyReconciliations.map(y => y.Reg).indexOf(x) < 0);
    }

    get vehicleDeparturesFromKeyChecksSource(){
        return this.vehicleDeparturesFromKeyChecks
        .filter(x => this.keyReconciliations.map(y => y.Reg).indexOf(x) < 0);
    }

    get vehiclesMissedLockedChecks(){
        // out of those vehicles that were available at check time and are still available 
        // return those that have not been locked checked in
        // and those that have not been reconciliated
        return this.vehiclesFromLockedCheckStillAvailable
        .filter(x => this.successfulLockedChecks.map(y => y.reg).indexOf(x) < 0)
        .filter(x => this.lockedReconciliations.map(y => y.Reg).indexOf(x) < 0);
    }

    get vehicleArrivalsFromLockedChecksSource(){
        return this.vehicleArrivalsFromLockedChecks
        .filter(x => this.lockedReconciliations.map(y => y.Reg).indexOf(x) < 0);
    }

    get vehicleDeparturesFromLockedChecksSource(){
        return this.vehicleDeparturesFromLockedChecks
        .filter(x => this.lockedReconciliations.map(y => y.Reg).indexOf(x) < 0);
    }

    isRightCheckType(checkDto: CheckDto, checkType: string | null): boolean{
        if (!checkType || checkType.length === 0){
            return true;
        }

        return checkDto.check_type === checkType;
    }

    mapVehicleCheck(fleetVehicleCheck: VehicleFleetCheckApiResponse): CheckDto[]{
        return [
            {
                id: fleetVehicleCheck.id,
                reg: fleetVehicleCheck.reg,
                last_updated_date: fleetVehicleCheck.last_updated_date,
                check_in_location: fleetVehicleCheck.checkin_location,
                rentman_location: fleetVehicleCheck.location,
                created_date: fleetVehicleCheck.created_date,

                check_in_date: fleetVehicleCheck.checkin_date,
                check_in_user: fleetVehicleCheck.checkin_user,
                check_status: this.mapKeyPresenceCheckStatus(fleetVehicleCheck.checkin_status),
                check_type: "Vehicle Presence Check"
            },
            {
                id: fleetVehicleCheck.id,
                reg: fleetVehicleCheck.reg,
                last_updated_date: fleetVehicleCheck.last_updated_date,
                check_in_location: fleetVehicleCheck.checkin_location,
                rentman_location: fleetVehicleCheck.location,
                created_date: fleetVehicleCheck.created_date,
                
                check_in_date: fleetVehicleCheck.vehicle_key_check_date,
                check_in_user: fleetVehicleCheck.vehicle_key_checked_by,
                check_status: this.mapKeyCheckStatus(fleetVehicleCheck.vehicle_key_is_present),
                check_type: "Vehicle Key Check"
            },
            {
                id: fleetVehicleCheck.id,
                reg: fleetVehicleCheck.reg,
                last_updated_date: fleetVehicleCheck.last_updated_date,
                check_in_location: fleetVehicleCheck.checkin_location,
                rentman_location: fleetVehicleCheck.location,
                created_date: fleetVehicleCheck.created_date,
                
                check_in_date: fleetVehicleCheck.vehicle_locked_check_date,
                check_in_user: fleetVehicleCheck.vehicle_locked_checked_by,
                check_status: this.mapLockedCheckStatus(fleetVehicleCheck.vehicle_is_locked),
                check_type: "Vehicle Locked Check"
            }         
        ];
    }

    mapVehicleReconciliation(fleetVehicleCheck: VehicleFleetCheckApiResponse): ReconciliationDto[]{
        return [
            {
                Reg: fleetVehicleCheck.reg,
                created_date: fleetVehicleCheck.created_date,
                location: fleetVehicleCheck.checkin_location,

                checkin_date: fleetVehicleCheck.checkin_date,
                vehicle_presence_check_reconciliation_by: fleetVehicleCheck.vehicle_presence_check_reconciliation_by,
                vehicle_presence_check_reconciliation_date: fleetVehicleCheck.vehicle_presence_check_reconciliation_date,
                vehicle_presence_check_reconciliation_outcome: fleetVehicleCheck.vehicle_presence_check_reconciliation_outcome,
                check_type: "Vehicle Presence Check"
            },
            {
                Reg: fleetVehicleCheck.reg,
                created_date: fleetVehicleCheck.created_date,
                location: fleetVehicleCheck.checkin_location,

                checkin_date: fleetVehicleCheck.checkin_date,
                vehicle_key_check_reconciliation_by: fleetVehicleCheck.vehicle_key_check_reconciliation_by,
                vehicle_key_check_reconciliation_date: fleetVehicleCheck.vehicle_key_check_reconciliation_date,
                vehicle_key_check_reconciliation_outcome: fleetVehicleCheck.vehicle_key_check_reconciliation_outcome,
                check_type: "Vehicle Key Check"
            },
            {
                Reg: fleetVehicleCheck.reg,
                created_date: fleetVehicleCheck.created_date,
                location: fleetVehicleCheck.checkin_location,

                checkin_date: fleetVehicleCheck.checkin_date,
                vehicle_is_locked_check_reconciliation_by: fleetVehicleCheck.vehicle_is_locked_check_reconciliation_by,
                vehicle_is_locked_check_reconciliation_date: fleetVehicleCheck.vehicle_is_locked_check_reconciliation_date,
                vehicle_is_locked_check_reconciliation_outcome: fleetVehicleCheck.vehicle_is_locked_check_reconciliation_outcome,
                check_type: "Vehicle Locked Check"
            }         
        ];
    }

    mapKeyPresenceCheckStatus(status: string | null){
        if (status === null || status.length === 0){
            return "Vehicle Present"
        }

        return status;
    }

    mapKeyCheckStatus(keyIsPresent: boolean | null){
        if (keyIsPresent === null){
            return "Key Check Pending"
        }

        return keyIsPresent ? "Key Present" : "Key Not Present";
    }

    mapLockedCheckStatus(vehicleIsLocked: boolean | null){
        if (vehicleIsLocked === null){
            return "Locked Check Pending"
        }

        return vehicleIsLocked ? "Vehicle Locked" : "Vehicle Not Locked";
    }

    formatDateTime(date: string){
        if (!date){
            return "";
        }
        const fmt = new Intl.DateTimeFormat("en-GB", { 
            year: "numeric", 
            month: "2-digit", 
            day: "2-digit", 
            hour: "2-digit", 
            minute: "2-digit", 
            second: "2-digit", 
            hour12: false }
        );

        return fmt.format(new Date(date)).replace(",", "");
    }

    formatTime(date: string){
        const fmt = new Intl.DateTimeFormat("en-GB", {
            hour: "2-digit", 
            minute: "2-digit", 
            second: "2-digit", 
            hour12: false }
        );

        return fmt.format(new Date(date)).replace(",", "");
    }
    

    displayReconciliationOutcome(outcome: string, rentmanLocation: string | undefined): string {
        if(!outcome){
            return "";
        }
        if(outcome === 'Present'){
            return outcome;
        }
        else if(outcome === 'Missing'){
            return outcome;
        }

        const isOnRentAtAnotherBranchMatch = outcome.startsWith("OnRentBranch:")
        if(isOnRentAtAnotherBranchMatch){
            const value = outcome.split(":")[2];
            const externalBranch = outcome.split(":")[1];
            return `On Rent at ${externalBranch} - ${value}`;
        }

        const isOnExternalBranchMatch = outcome.startsWith("Branch:");
        if(isOnExternalBranchMatch){
            const value = outcome.split(":")[1];
            return `At ${value} Branch`;
        }

        const isOnRentMatch = outcome.startsWith("OnRent:");
        if(isOnRentMatch && rentmanLocation !== undefined){
            const value = outcome.split(":")[1];
            return `On Rent at ${rentmanLocation} - ${value}`;
        }

        const match = vehiclePresenceOutcomes.find(x => outcome.indexOf(x.value) > -1);
        if (match){
            return `${match.name}`;
        }

        return outcome;
    }

    containsReconciliation(fleetCheck: VehicleFleetCheckApiResponse): boolean{
        const dto: ReconciliationDto = {
            Reg: fleetCheck.reg,
            checkin_date: fleetCheck.checkin_date,
            location: fleetCheck.location,
            created_date: fleetCheck.created_date,
            vehicle_presence_check_reconciliation_outcome: fleetCheck.vehicle_presence_check_reconciliation_outcome,
            vehicle_presence_check_reconciliation_date: fleetCheck.vehicle_presence_check_reconciliation_date,
            vehicle_presence_check_reconciliation_by: fleetCheck.vehicle_presence_check_reconciliation_by,

            vehicle_key_check_reconciliation_outcome: fleetCheck.vehicle_key_check_reconciliation_outcome,
            vehicle_key_check_reconciliation_date: fleetCheck.vehicle_key_check_reconciliation_date,
            vehicle_key_check_reconciliation_by: fleetCheck.vehicle_key_check_reconciliation_by,

            vehicle_is_locked_check_reconciliation_outcome: fleetCheck.vehicle_is_locked_check_reconciliation_outcome,
            vehicle_is_locked_check_reconciliation_date: fleetCheck.vehicle_is_locked_check_reconciliation_date,
            vehicle_is_locked_check_reconciliation_by: fleetCheck.vehicle_is_locked_check_reconciliation_by,
            check_type: "",
        };
        return this.containsReconciliationOutcome(dto);
    }

    retrieveActiveLocationChecks(){
        const account = this.authService.instance.getAllAccounts()[0];
        const accessTokenRequest = {
            scopes: ["user.read"],
            account: account,
            };

        this.authService.acquireTokenSilent(accessTokenRequest).subscribe(x => {
            const accessToken = x.accessToken;
            this.loading++;
            //If the selected date is not a scheduled day (or it is an exception) at the given branch
            //Abort and display a warning message saying that no data can be retrieved for that date
            //because it is not a scheduled day
            this.retrieveActiveBranches(accessToken, this.selectedYear, this.selectedMonth, this.selectedDay).subscribe(
                response => {
                    this.loading--;
                    this.onActiveBranchesReceived(this.locationSelected, response);
                    if(this.isScheduledDateSelected){
                        this.retrieveLocationChecks();
                    }
                }
            );
        });
    }

    retrieveLocationChecks() {
        this.loading++;
        const account = this.authService.instance.getAllAccounts()[0];
        const accessTokenRequest = {
            scopes: ["user.read"],
            account: account,
            };

        this.authService.acquireTokenSilent(accessTokenRequest).subscribe(x => {
            const accessToken = x.accessToken;

            var apiCallObservable = this.getTodaysVehicleChecks(accessToken, this.locationSelected);
            
            if(!this.selectedDateIsToday){
                apiCallObservable = this.getVehicleChecksByDate(accessToken, this.locationSelected, this.selectedYear, this.selectedMonth, this.selectedDay);
            }

            this.getSessionCheckByDate(accessToken, this.locationSelected, this.selectedYear, this.selectedMonth, this.selectedDay)
            .subscribe(x => {
                if (x.status_code != 400){
                    this.sessionCheck = x.result;
                }
            }, errorResponse => {
                this.sessionCheck = null;
            });

            this.getUncheckedVechicles(accessToken, this.locationSelected, this.selectedYear, this.selectedMonth, this.selectedDay)
            .subscribe(x => {
                if(x.status_code != 400){
                    this.uncheckedVehicles = x.result;
                }
            });

            this.getLocationMismatchedChecks(accessToken, this.locationSelected, this.selectedYear, this.selectedMonth, this.selectedDay)
            .subscribe(x => {
                if(x.status_code != 400){
                    const result = x.result as VehicleFleetCheckApiResponse[];
                    const checks = result.map(x => this.mapVehicleCheck(x)).flatMap(x => x);
                    const presenceChecks = checks.filter(x => x.check_type === "Vehicle Presence Check");
                    this.locationMismatchedPresenceChecks = presenceChecks;

                    // some of the results are simply not reconciled but the map will always return a reconciliation dto
                    // so we need to filter out those that have no reconciliation outcome
                    const reconciliations = result.filter(x => this.containsReconciliation(x)).map(x => this.mapVehicleReconciliation(x)).flatMap(x => x);
                    const presenceReconciliations = reconciliations.filter(x => x.check_type === "Vehicle Presence Check");
                    this.locationMismatchedPresenceReconciliations = presenceReconciliations;
                }
            });

            this.getUnaccountedRegs(accessToken, this.locationSelected, this.selectedYear, this.selectedMonth, this.selectedDay)
            .subscribe(x => {
                if (x.result.length > 0){
                    if(x.result[0].PastUnavailablePresentAvailable.length > 0 && x.result[0].PastAvailablePresentUnavailable.length > 0){
                        // this.vehicleArrivalsFromPresenceChecks = x.result[0].PastUnavailablePresentAvailable;
                        // this.vehicleDeparturesFromPresenceChecks = x.result[0].PastAvailablePresentUnavailable;
                        this.vehiclesFromPresenceCheckStillAvailable = x.result[0].StillAvailable;

                        this.vehicleArrivalsFromKeyChecks = x.result[0].PastUnavailablePresentAvailable;
                        this.vehicleDeparturesFromKeyChecks = x.result[0].PastAvailablePresentUnavailable;
                        this.vehiclesFromKeyCheckStillAvailable = x.result[0].StillAvailable;
                    }
                    else if(x.result.length > 1 && x.result[1].PastUnavailablePresentAvailable.length > 0 && x.result[1].PastAvailablePresentUnavailable.length > 0){
                        // this.vehicleArrivalsFromKeyChecks = x.result[1].PastUnavailablePresentAvailable;
                        // this.vehicleDeparturesFromKeyChecks = x.result[1].PastAvailablePresentUnavailable;
                        // this.vehiclesFromKeyCheckStillAvailable = x.result[1].StillAvailable;

                        this.vehicleArrivalsFromLockedChecks = x.result[1].PastUnavailablePresentAvailable;
                        this.vehicleDeparturesFromLockedChecks = x.result[1].PastAvailablePresentUnavailable;
                        this.vehiclesFromLockedCheckStillAvailable = x.result[1].StillAvailable;
                    }
                    else { //if no vehicle movements happen - just return all branch vehicles
                        this.vehiclesFromPresenceCheckStillAvailable = x.result[0].StillAvailable;
                        this.vehiclesFromKeyCheckStillAvailable = x.result[0].StillAvailable;
                        this.vehiclesFromLockedCheckStillAvailable = x.result[0].StillAvailable;
                    }
                    // else if(x.result[2].PastUnavailablePresentAvailable && x.result[2].PastAvailablePresentUnavailable){
                    //     this.vehicleArrivalsFromLockedChecks = x.result[2].PastUnavailablePresentAvailable;
                    //     this.vehicleDeparturesFromLockedChecks = x.result[2].PastAvailablePresentUnavailable;
                    //     this.vehiclesFromLockedCheckStillAvailable = x.result[2].StillAvailable;
                    // }
                }
            });

            apiCallObservable.subscribe(response => {
                this.onVehicleChecksReceived(response); 
            });
        });  
    }

    onActiveBranchesReceived(location: string, response: IApiResponse<BranchSchedule[]>){
        if (!response.result){
            return;
        }

        const matchingActiveBranch = response.result.find(x => x.location === location);
        if(matchingActiveBranch == undefined){
            this.notificationService.warn(`The selected date is not a scheduled date at ${this.locationSelected}`);
            return;
        }

        this.isScheduledDateSelected = true;
    }

    retrieveActiveBranches(accessToken: string, year: number, month: number, day: number){

        const url = environment.apiUrl + `api/v2/branchschedules/Active?year=${year}&month=${month}&day=${day}`;
        const getObservable = this.httpservice.get<any>(
            url,
            {
                headers: { 
                    "Authorization": accessToken,
                    'Accept': 'application/json', 
                },
                'responseType': 'json'
            },
        );
        return getObservable;
    }

    onVehicleChecksReceived(vehicleCheckResponse: ApiResponse<VehicleFleetCheckApiResponse[]>){
        this.vehicleChecks = vehicleCheckResponse.result;
        this.loading--;
    }

    getUncheckedVechicles(accessToken: string, location: string, year: number, month: number, day: number){

        const url = environment.apiUrl + `api/v2/closingchecks/MissingRegsFromChecks?location=${location}&year=${year}&month=${month}&day=${day}`;
        const getObservable = this.httpservice.get<any>(
            url,
            {
                headers: { 
                    "Authorization": accessToken,
                    'Accept': 'application/json', 
                },
                'responseType': 'json'
            },
        );
        return getObservable;
    }

    getLocationMismatchedChecks(accessToken: string, location: string, year: number, month: number, day: number){

        const url = environment.apiUrl + `api/v2/closingchecks/LocationMismatchedChecks?location=${location}&year=${year}&month=${month}&day=${day}`;
        const getObservable = this.httpservice.get<any>(
            url,
            {
                headers: { 
                    "Authorization": accessToken,
                    'Accept': 'application/json', 
                },
                'responseType': 'json'
            },
        );
        return getObservable;
    }

    getUnaccountedRegs(accessToken: string, location: string, year: number, month: number, day: number){

        const url = environment.apiUrl + `api/v2/closingchecks/UnaccountedRegs?location=${location}&year=${year}&month=${month}&day=${day}`;
        const getObservable = this.httpservice.get<any>(
            url,
            {
                headers: { 
                    "Authorization": accessToken,
                    'Accept': 'application/json', 
                },
                'responseType': 'json'
            },
        );
        return getObservable;
    }
    
    getTodaysVehicleChecks(accessToken: string, location: string){

        const url = environment.apiUrl + `api/v2/closingchecks/TodaysVehicleChecksByLocation?location=${location}}`;
        const getObservable = this.httpservice.get<any>(
            url,
            {
                headers: { 
                    "Authorization": accessToken,
                    'Accept': 'application/json', 
                },
                'responseType': 'json'
            },
        );
        return getObservable;
    }

    getVehicleChecksByDate(accessToken: string, location: string, year: number, month: number, day: number){

        const url = environment.apiUrl + `api/v2/closingchecks/GetVehicleChecksByLocationAndDate?location=${location}&year=${year}&month=${month}&day=${day}`;
        const getObservable = this.httpservice.get<any>(
            url,
            {
                headers: { 
                    "Authorization": accessToken,
                    'Accept': 'application/json', 
                },
                'responseType': 'json'
            },
        );
        return getObservable;
    }

   getSessionCheckByDate(accessToken: string, location: string, year: number, month: number, day: number){

        const url = environment.apiUrl + `api/v2/closingchecks/GetSessionCheckByLocationAndDate?location=${location}&year=${year}&month=${month}&day=${day}`;
        const getObservable = this.httpservice.get<any>(
            url,
            {
                headers: { 
                    "Authorization": accessToken,
                    'Accept': 'application/json', 
                },
                'responseType': 'json'
            },
        );
        return getObservable;
    } 

}